<template>
  <div v-if="isInitialTopicsLoaded">
    <template v-if="hasTopics">
      <div class="d-flex mb-2 ga-4 justify-space-between">
        <Permission permission="topic:write">
          <div
            class="d-flex flex-grow-1"
            :class="{
              'flex-column': $vuetify.display.xs,
              'mb-6': $vuetify.display.smAndDown,
            }"
          >
            <v-btn
              color="primary"
              :loading="isPerformingAction('generatingTopic')"
              :disabled="isPerformingAction('generatingTopic', true)"
              data-prodcust-fruits="topics-generate"
              data-testid="topics-generate-topic"
              @click="generateTopic"
            >
              <v-icon start> mdi-auto-fix </v-icon>
              {{ $t('topics.actions.generate') }}
            </v-btn>

            <v-btn
              color="primary"
              :class="{
                'mx-4 mr-2': $vuetify.display.smAndUp,
                'mt-2': $vuetify.display.xs,
              }"
              :disabled="isPerformingAction('creatingTopic', true)"
              data-prodcust-fruits="topics-create"
              data-testid="topics-create-topic-button"
              @click="startCreateTopic"
            >
              <v-icon start> mdi-plus </v-icon>
              {{ $t('topics.actions.create') }}
            </v-btn>
          </div>
        </Permission>
        <Feature :feature-slug="featureNames.TOPIC_SETTINGS">
          <div class="md:mb-0">
            <TopicSettings @create-topic="generateTopic" />
          </div>
        </Feature>
      </div>

      <v-window class="py-4 transparent">
        <v-window-item>
          <v-scroll-y-transition group>
            <!-- Errors -->
            <v-row
              v-if="error"
              :key="error"
            >
              <v-col>
                <MaxActiveTopicsReachedAlert
                  v-if="error === 'MAX_ACTIVE_TOPICS_REACHED'"
                  :max-active-topics="maxActiveTopics"
                  @dismiss="resetError"
                />
                <GenericErrorAlert
                  v-else
                  :error="error"
                  @dismiss="resetError"
                />
              </v-col>
            </v-row>

            <!-- Active topics -->
            <v-row
              v-for="topic in activeTopics"
              :key="topic.id"
            >
              <v-col>
                <TopicCardAnimated
                  v-bind="topic"
                  @edit="openEditTopicDialog"
                />
              </v-col>
            </v-row>
            <div
              class="pt-16"
              v-if="archivedTopics.length > 0"
              data-testid="topics-archived-section"
            >
              <p class="font-weight-bold">Archived Marketing-Topics</p>
              <BlurredBackdropUpselling :is-blurred="!mayUnarchiveTopics">
                <v-row
                  v-for="topic in archivedTopics"
                  :key="topic.id"
                >
                  <v-col>
                    <TopicCard
                      v-bind="topic"
                      @update="reloadQuery"
                      @error="handleError"
                      @edit="openEditTopicDialog"
                    />
                  </v-col>
                </v-row>
              </BlurredBackdropUpselling>
            </div>
          </v-scroll-y-transition>
        </v-window-item>
      </v-window>

      <v-row>
        <v-col class="pa-8">
          {{ $t('topics.limit-note.line1.0') }} <strong>{{ activeTopics.length }}</strong>
          {{ $t('topics.limit-note.line1.1') }}<br />
          {{ $t('topics.limit-note.line2.0') }} <strong>{{ maxActiveTopics }}</strong>
          {{ $t('topics.limit-note.line2.1') }}<br />
          {{ $t('topics.limit-note.line3.0') }}
          <!-- Use wrapper span to track the event as @click is not called on <router-link> -->
          <span
            @click="$tracking.event('Marketing Topics', 'Clicked', 'MaxActiveTopicsReachedAlert')"
          >
            <a
              v-if="isPlatformSmg || isPlatformTurkoglu"
              :href="upgradePackageUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('topics.limit-note.line3.1') }}
            </a>
            <router-link
              v-else
              :to="upgradeLink"
            >
              {{ $t('topics.limit-note.line3.1') }}
            </router-link>
          </span>
        </v-col>
      </v-row>
    </template>

    <div
      v-else
      class="d-flex justify-center align-center flex-column"
      :class="{
        'py-6': $vuetify.display.xs,
        'py-16': $vuetify.display.smAndUp,
      }"
    >
      <div class="welcome-box">
        <p class="text--secondary">
          {{ $t('topics.welcome.p1') }}
        </p>
        <p class="text--secondary">
          {{ $t('topics.welcome.p2') }}
        </p>
        <v-btn
          size="x-large"
          color="primary"
          class="mt-10"
          :loading="isPerformingAction('generatingInitialTopics')"
          data-testid="topics-generate-initial-topics"
          @click="generateInitialTopics"
        >
          <v-icon start> mdi-auto-fix </v-icon>
          {{ $t('topics.welcome.cta') }}
        </v-btn>
      </div>
    </div>

    <TopicDialog
      v-model="dialogVisible"
      :topic="selectedTopic"
      :is-editing="isEditingTopic"
      @reload="reloadQuery"
      @error="handleError"
    />
  </div>
</template>

<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling'
import Permission from '@/components/Permission'
import bus, { eventNames } from '@/lib/eventBus'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import performingActionMixin from '@/mixins/performingAction'

import GenericErrorAlert from './GenericErrorAlert'
import GENERATE_INITIAL_TOPICS from './graphql/generateInitialTopics.gql'
import GENERATE_TOPIC from './graphql/generateTopic.gql'
import GET_TOPICS from './graphql/getTopics.gql'
import MaxActiveTopicsReachedAlert from './MaxActiveTopicsReachedAlert'
import TopicCard from './TopicCard'
import TopicCardAnimated from './TopicCardAnimated'
import TopicDialog from './TopicDialog.vue'
import TopicSettings from './TopicSettings.vue'
import TopicStatus from './TopicStatus'

// Tab indexes
// Didn't use # routes as it didn't really work switching tabs programmatically.
const TAB_ACTIVE = 0
const TAB_ARCHIVED = 1

export default {
  components: {
    TopicCard,
    TopicCardAnimated,
    BlurredBackdropUpselling,
    Permission,
    GenericErrorAlert,
    MaxActiveTopicsReachedAlert,
    TopicSettings,
    TopicDialog,
  },

  mixins: [featureMixin, performingActionMixin, brandingMixin],

  data: () => ({
    editTopic: null,
    TAB_ACTIVE,
    TAB_ARCHIVED,
    topics: [],
    showCreateTopic: false,
    isInitialTopicsLoaded: false,
    error: null,
    // New properties for dialog
    dialogVisible: false,
    selectedTopic: null,
    isEditingTopic: false,
  }),

  apollo: {
    topics: {
      query: GET_TOPICS,
      result({ loading }) {
        if (!loading) {
          this.isInitialTopicsLoaded = true
        }
      },
      cachePolicy: 'cache-and-network',
    },
  },

  computed: {
    upgradeLink() {
      return {
        name: 'Settings',
        params: { section: 'contract' },
        query: { showUpgrade: '1' },
      }
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    config() {
      return this.getFeature('topic').config
    },

    maxActiveTopics() {
      return this.config.maxActiveTopics
    },

    mayUnarchiveTopics() {
      return this.config.mayUnarchiveTopics
    },

    activeTopics() {
      return this.topics.filter(
        (topic) => topic.status !== TopicStatus.ARCHIVED && topic.visibility?.topics === true
      )
    },

    archivedTopics() {
      const filteredTopics = this.topics.filter(
        (topic) => topic.status === TopicStatus.ARCHIVED && topic.visibility?.topics === true
      )

      if (!this.mayUnarchiveTopics) {
        return filteredTopics.slice(0, 3)
      }

      return filteredTopics
    },

    hasTopics() {
      return this.topics.length > 0
    },
  },

  methods: {
    /**
     * Open the dialog to create a new topic
     */
    startCreateTopic() {
      this.resetError()
      this.selectedTopic = null
      this.isEditingTopic = false
      this.dialogVisible = true
      this.$tracking.event('Marketing Topics', 'Clicked', 'Own Topic Create')
    },

    /**
     * Open the dialog to edit an existing topic
     */
    openEditTopicDialog(topic) {
      this.resetError()
      this.selectedTopic = this.topics.find((t) => t.id === topic.id) || null // Ensure we have full data about topic
      this.isEditingTopic = true
      this.dialogVisible = true
      const label = topic.isSystemGenerated ? 'Topic Edit' : 'Own Topic Edit'
      this.$tracking.event('Marketing Topics', 'Clicked', label)
    },

    /**
     * Handle a newly created topic
     */
    handleTopicCreated(topic) {
      this.$tracking.event('Marketing Topics', 'Clicked', 'Own Topic Save')

      // Update the cache with the new topic
      const { topics: cachedTopics } = this.$apollo.client.readQuery({ query: GET_TOPICS })

      this.$apollo.client.writeQuery({
        query: GET_TOPICS,
        data: { topics: [topic, ...cachedTopics] },
      })
    },
    async reloadQuery() {
      await this.$apollo.queries.topics.refetch()
      await this.$nextTick()
      if (this.selectedTopic && this.selectedTopic.status === TopicStatus.ARCHIVED) {
        const topic = this.topics.find((topic) => topic.id === this.selectedTopic.id)
        this.openEditTopicDialog(topic)
      }
    },

    handleTopicArchived(topic) {
      const label = topic.isSystemGenerated ? 'Topic Archive' : 'Own Topic Archive'
      this.$tracking.event('Marketing Topics', 'Clicked', label)

      const { topics: cachedTopics } = this.$apollo.client.readQuery({ query: GET_TOPICS })

      const updatedTopics = cachedTopics.map((cachedTopic) =>
        cachedTopic.id === topic.id ? topic : cachedTopic
      )

      this.$apollo.client.writeQuery({
        query: GET_TOPICS,
        data: { topics: updatedTopics },
      })
    },

    cancelCreateTopic() {
      this.showCreateTopic = false
    },

    /**
     * Is called e.g. when the tab changes and some state should be reset.
     */

    async generateTopic() {
      this.$tracking.event('Marketing Topics', 'Clicked', 'Generation')

      this.resetError()

      await this.withPerformingAction('generatingTopic', async () => {
        try {
          await this.$apollo.mutate({
            mutation: GENERATE_TOPIC,
            variables: {
              isMasterView: this.isMasterView,
            },
            update: (cache, { data: { topic } }) => {
              const { topics: cachedTopics } = cache.readQuery({ query: GET_TOPICS })
              cache.writeQuery({
                query: GET_TOPICS,
                data: { topics: [topic, ...cachedTopics] },
              })
            },
          })
          this.$tracking.event('Marketing Topics', 'Generation', 'Generation')
          this.$tracking.heapEvent('topic_created')
        } catch (err) {
          this.handleGraphQLError(err)
        }
      })
    },

    async generateInitialTopics() {
      this.resetError()
      this.$tracking.event('Marketing Topics', 'Clicked', 'Initial Generation')

      await this.withPerformingAction('generatingInitialTopics', async () => {
        try {
          const { data } = await this.$apollo.mutate({
            mutation: GENERATE_INITIAL_TOPICS,
            update: (cache, { data: { topics } }) => {
              const { topics: cachedTopics } = cache.readQuery({ query: GET_TOPICS })
              cache.writeQuery({
                query: GET_TOPICS,
                data: { topics: [...topics, ...cachedTopics] },
              })
            },
          })
          this.$tracking.event('Marketing Topics', 'Generation', 'Initial Generation')
          // One heap event per generated topic, so the aggregate topic_created
          // count reflects actual topics created, not generation actions.
          data.topics.forEach(() => this.$tracking.heapEvent('topic_created'))
          bus.$emit(eventNames.TRIGGER_ANIMATION)
        } catch (err) {
          this.handleGraphQLError(err)
        }
      })
    },

    /**
     * Add a new topic to the cache.
     * This method is called as a response to an action on a TopicCard.
     */
    addTopicToCache(topic, cache) {
      this.cancelCreateTopic()

      const { topics: cachedTopics } = cache.readQuery({ query: GET_TOPICS })

      cache.writeQuery({
        query: GET_TOPICS,
        data: { topics: [topic, ...cachedTopics] },
      })
    },

    /**
     * Update the given topic in the cache.
     * This method is called as a response to an action on a TopicCard.
     */
    updateTopicInCache(topic, cache) {
      const { topics: cachedTopics } = cache.readQuery({ query: GET_TOPICS })

      // Create a new array with the updated topic
      const updatedTopics = cachedTopics.map((cachedTopic) =>
        cachedTopic.id === topic.id ? topic : cachedTopic
      )

      // Write back to cache
      cache.writeQuery({
        query: GET_TOPICS,
        data: { topics: updatedTopics },
      })
    },

    handleGraphQLError(err) {
      const graphQLError = err.graphQLErrors[0]
      // First we handle specific errors that we expect.
      if (graphQLError?.extensions.code === 'MAX_ACTIVE_TOPICS_REACHED') {
        this.handleError(graphQLError.extensions.code)
        // Otherwise we show the user any message that comes from the server as we cannot further predict the error.
        // It could be a forbidden error though, but that should not arise as long as the frontend is correctly implemented.
      } else {
        this.handleError(err.message)
      }
    },

    handleError(error) {
      this.resetError()
      this.$nextTick(() => {
        this.error = error.replace('GraphQL error: ', '')
      })
    },

    resetError() {
      this.error = null
    },
  },
  watch: {
    dialogVisible: {
      handler(value) {
        if (!value) this.selectedTopic = null
      },
    },
  },
}
</script>

<style scoped>
.error-text {
  max-width: 800px;
}

.welcome-box {
  max-width: 600px;
  text-align: center;
  font-size: 1.2em;
}
</style>
